// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { triggerActiveSessionRefresh } from './useActiveSession';
import { useNavigationBlocker } from './useNavigationBlocker';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { useGameStore } from '../store/gameStore';
import { useSettingsStore } from '../store/settingsStore';
import { ClientActionEnum, type LobbyState } from '../types/game';
import type { Character } from '../types/terriblePsychiatrist';

export function useLobbyActions() {
  const { sessionId, roomCode, playerId, gameState, updateGameState, spectators, allowSpectators } =
    useGameStore();
  const lobbyState = gameState as LobbyState | null;
  const { sendMessage } = useWebSocketContext();
  const {
    currentSettings,
    fetchPresets,
    fetchSchema,
    setSettings,
    availablePresets,
  } = useSettingsStore();
  const navigate = useNavigate();

  const [isStarting, setIsStarting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(() => {
    // Read from localStorage for global advanced mode
    // Wrapped in try-catch for private browsing mode compatibility
    try {
      return localStorage.getItem('advancedMode') === 'true';
    } catch {
      return false;
    }
  });
  const [copied, setCopied] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [showNpcs, setShowNpcs] = useState(false);
  const [copiedClaimCode, setCopiedClaimCode] = useState<string | null>(null);
  const [selectingCharacter, setSelectingCharacter] = useState(false);

  // Block browser navigation (back button) and show confirmation
  useNavigationBlocker({
    when: !allowNavigation,
    onBlock: () => {
      setShowLeaveConfirm(true);
      return false;
    },
    message: 'Are you sure you want to leave the lobby?',
  });

  // Keyboard shortcut: "^" (Shift+6) toggles advanced mode globally
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === '^' || (e.shiftKey && e.key === '6')) {
        e.preventDefault();
        setAdvancedMode(prev => {
          const newValue = !prev;
          try {
            localStorage.setItem('advancedMode', String(newValue));
          } catch {
            // localStorage unavailable (private browsing mode)
          }
          return newValue;
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Fetch presets and schema when game changes
  useEffect(() => {
    if (lobbyState?.game_id) {
      fetchPresets(lobbyState.game_id);
      fetchSchema(lobbyState.game_id);
    }
  }, [lobbyState?.game_id, fetchPresets, fetchSchema]);

  // Sync lobby settings from game state
  useEffect(() => {
    if (lobbyState?.lobby_settings) {
      setSettings(lobbyState.lobby_settings);
    }
  }, [lobbyState?.lobby_settings, setSettings]);

  const currentPlayers = lobbyState?.current_players ?? [];
  const isHost = lobbyState?.is_host ?? currentPlayers[0]?.player_id === playerId;

  const extendedPhase = (lobbyState as { phase?: string } | null)?.phase;
  const effectiveRoomStatus = lobbyState?.room_status || extendedPhase;

  // Defensive check: if game has started, navigate to game
  useEffect(() => {
    if (!sessionId) return;

    const isGameStarted = effectiveRoomStatus && effectiveRoomStatus !== 'lobby';

    if (isGameStarted) {
      logger.info('lobby_detected_game_started', {
        roomStatus: effectiveRoomStatus,
        phase: extendedPhase,
        navigatingTo: `/game/${sessionId}`,
      });
      setAllowNavigation(true);
      navigate(`/game/${sessionId}`);
    }
  }, [effectiveRoomStatus, extendedPhase, sessionId, navigate]);

  // Format room code for display - handles both dash format and session IDs
  const getDisplayRoomCode = (): string => {
    if (!roomCode) {
      return 'Loading...';
    }

    const parts = roomCode.split('-');
    if (parts.length === 6) {
      return roomCode;
    }

    if (/^[A-Z0-9]{4}-[A-Z0-9]{4}$/i.test(roomCode)) {
      return roomCode;
    }

    logger.warn('lobby_invalid_room_code', { roomCode, sessionId });
    return roomCode;
  };

  // Generate the join link
  const getJoinLink = () => {
    if (!roomCode) return window.location.origin;
    return `${window.location.origin}/magic/${roomCode}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getJoinLink());
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('clipboard_copy_failed', { error: err });
      toast.error('Failed to copy link');
    }
  };

  const handleLeaveRoom = async () => {
    setIsLeaving(true);
    setShowLeaveConfirm(false);
    setAllowNavigation(true);

    try {
      await api.leaveMySession();
      triggerActiveSessionRefresh();
      logger.info('room_left', { sessionId, playerId });
    } catch (error) {
      logger.error('leave_room_failed', { error, sessionId, playerId });
    }

    useGameStore.getState().clearSession();
    navigate('/', { replace: true });
  };

  const handleStartGame = async () => {
    if (!isHost) {
      toast.error('Only the host can start the game');
      return;
    }

    setIsStarting(true);
    try {
      sendMessage(ClientActionEnum.START_GAME);
    } catch (error) {
      logger.error('game_start_failed', { error });
      toast.error('Failed to start game. Check game requirements.');
    } finally {
      setIsStarting(false);
    }
  };

  const handleTogglePrivacy = async () => {
    if (!lobbyState?.is_host) {
      toast.error('Only the host can change privacy settings');
      return;
    }

    const newPrivacy = !(lobbyState.is_public ?? true);
    try {
      await api.toggleRoomPrivacy(sessionId!, newPrivacy);

      updateGameState({
        ...lobbyState,
        is_public: newPrivacy,
      });

      toast.success(newPrivacy ? 'Room is now public' : 'Room is now private');
    } catch (error) {
      logger.error('privacy_toggle_failed', { error });
      toast.error('Failed to update privacy setting');
    }
  };

  const handleToggleSpectators = () => {
    if (!lobbyState?.is_host) {
      toast.error('Only the host can change spectator settings');
      return;
    }

    const newAllow = !allowSpectators;
    sendMessage('TOGGLE_SPECTATORS', { allow_spectators: newAllow });
    logger.info('spectators_toggled', { allow: newAllow });
  };

  const handleCopyClaimCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedClaimCode(code);
      toast.success('Claim code copied!');
      setTimeout(() => setCopiedClaimCode(null), 2000);
    } catch (err) {
      logger.error('claim_code_copy_failed', { error: err });
      toast.error('Failed to copy claim code');
    }
  };

  const handlePresetSelect = (presetName: string) => {
    if (!lobbyState?.is_host) {
      toast.error('Only the host can change game settings');
      return;
    }

    const preset = availablePresets.find(value => value.name === presetName);
    if (!preset) {
      toast.error(`Preset "${presetName}" is unavailable.`);
      return;
    }

    sendMessage(ClientActionEnum.UPDATE_LOBBY_SETTINGS, {
      settings: preset.settings,
    });
    logger.info('preset_selected', { presetName });
  };

  const handleSaveSettings = (settings: Record<string, unknown>) => {
    if (!lobbyState?.is_host) {
      toast.error('Only the host can change game settings');
      return;
    }

    const mergedSettings = {
      ...(currentSettings || {}),
      ...settings,
    };

    logger.info('settings_save_requested', { settings: mergedSettings });
    sendMessage(ClientActionEnum.UPDATE_LOBBY_SETTINGS, {
      settings: mergedSettings,
    });
  };

  const handleSelectCharacter = useCallback(
    (characterId: string) => {
      if (selectingCharacter) return;
      setSelectingCharacter(true);
      logger.info('character_selected', { characterId });
      sendMessage(ClientActionEnum.SELECT_CHARACTER, {
        character_id: characterId,
      });
      setTimeout(() => setSelectingCharacter(false), 500);
    },
    [sendMessage, selectingCharacter]
  );

  // Extract character state from game state (for games like Terrible Psychiatrist)
  const rawState = lobbyState as Record<string, unknown> | null;
  const characters: Character[] = (rawState?.characters as Character[]) || [];
  const selectedCharacterId: string | null = (rawState?.my_character_id as string) || null;
  const allCharactersSelected: boolean = (rawState?.all_characters_selected as boolean) || false;
  const hasCharacterSelection = characters.length > 0;

  // Format game name for display
  const getGameName = (): string => {
    if (!lobbyState?.game_id) return 'Lobby';
    return lobbyState.game_id
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return {
    // Store values
    sessionId,
    roomCode,
    playerId,
    lobbyState,
    spectators,
    allowSpectators,
    currentSettings,
    // State
    isStarting,
    isLeaving,
    advancedMode,
    copied,
    showLeaveConfirm,
    setShowLeaveConfirm,
    showNpcs,
    setShowNpcs,
    copiedClaimCode,
    selectingCharacter,
    // Derived state
    currentPlayers,
    isHost,
    characters,
    selectedCharacterId,
    allCharactersSelected,
    hasCharacterSelection,
    // Helpers
    getDisplayRoomCode,
    getGameName,
    // Handlers
    handleCopyLink,
    handleLeaveRoom,
    handleStartGame,
    handleTogglePrivacy,
    handleToggleSpectators,
    handleCopyClaimCode,
    handlePresetSelect,
    handleSaveSettings,
    handleSelectCharacter,
  };
}
